/* 3D dental viewer — three.js, 3Shape/Exocad-style controls.
 *
 * INPUTS (two modes, both supported):
 *   1. Single-file (legacy):   <STLViewer url filename onClose />
 *   2. Multi-mesh (bite view): <STLViewer meshes={[{url, filename, label?, jaw?}, …]} onClose />
 *
 * When two meshes are loaded that already share spatial coordinates (the way a
 * dental scanner exports upper + lower jaws), they snap together in perfect
 * occlusion — exactly like Exocad/3Shape.
 *
 * FEATURES
 *   - Orbit controls (drag = rotate, scroll = zoom, right-drag = pan)
 *   - Material modes: Original (vertex colors · PLY/OBJ only) / Beige plaster / MatCap glossy
 *   - Per-mesh visibility + opacity sliders (independent)
 *   - View presets: Front · Left · Right · Top (occlusal) · Bottom · Iso
 *   - Wireframe toggle
 *   - Reset camera, download, fullscreen, close
 */
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { Loader2, Download, RotateCcw, ZoomIn, Maximize2, Minimize2, PictureInPicture2, X, Eye, EyeOff, Box as BoxIcon, Paintbrush, Sparkles, Grid3X3 } from "lucide-react";

// File extensions we know how to load.
const SUPPORTED = new Set(["stl", "ply", "obj"]);

const MATERIAL_MODES = [
    { id: "beige", label: "Beige", icon: Paintbrush, color: 0xe6d2b5, specular: 0x4a3f30, shininess: 25 },
    { id: "matcap", label: "Clay", icon: Sparkles, color: 0xeae0d0 },           // Exocad-style glossy
    { id: "original", label: "Original", icon: Eye, color: 0xffffff, specular: 0x222222, shininess: 50 },
];

// View presets, expressed as (azimuth, polar) angles in radians around the model.
const VIEW_PRESETS = [
    { id: "front", label: "Front", dir: [0, 0, 1] },
    { id: "left", label: "Left", dir: [-1, 0, 0] },
    { id: "right", label: "Right", dir: [1, 0, 0] },
    { id: "top", label: "Top", dir: [0, 1, 0.001] },     // Occlusal — tiny z to keep up-vector stable
    { id: "bottom", label: "Bottom", dir: [0, -1, 0.001] },
    { id: "iso", label: "Iso", dir: [0.7, 0.55, 1] },
];

// Heuristic: guess whether a filename refers to the upper or lower jaw.
// Scanners typically tag the file (`upper.stl`, `mandible.ply`, etc.).
function guessJaw(filename = "") {
    const f = filename.toLowerCase();
    if (/(upper|maxilla|max|top)/.test(f)) return "upper";
    if (/(lower|mandible|mand|bottom)/.test(f)) return "lower";
    return null;
}


export default function STLViewer({ url, filename, meshes, onClose }) {
    // Normalise to a single internal `items` array — legacy single-file calls
    // become an array of one so the rest of the component is shape-agnostic.
    const items = useMemo(() => {
        if (Array.isArray(meshes) && meshes.length > 0) return meshes;
        if (url) return [{ url, filename, label: filename }];
        return [];
    }, [url, filename, meshes]);

    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const rendererRef = useRef(null);
    const meshesRef = useRef([]);          // [{ key, threeMesh, ext, originalMaterial, beigeMaterial, matcapMaterial, hasColors, label, jaw }]
    const groupRef = useRef(null);
    const fitDistanceRef = useRef(140);
    const rafRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Display mode: 'inline' = embedded in page, 'fullscreen' = takes the whole
    // viewport, 'pip' = floating draggable+resizable window the user can keep
    // visible while scrolling other content. Only one mode is active at a time.
    const [mode, setMode] = useState("inline");
    const isFs = mode === "fullscreen";
    const isPip = mode === "pip";
    // PiP geometry — initialised bottom-right of the viewport with sensible
    // size. Persisted to localStorage so the technician keeps their layout.
    const [pipBox, setPipBox] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem("stlviewer_pip") || "null");
            if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) return saved;
        } catch (e) { /* ignore */ }
        const w = Math.min(560, Math.max(360, (typeof window !== "undefined" ? window.innerWidth : 1280) * 0.32));
        const h = Math.min(480, Math.max(280, w * 0.78));
        const x = (typeof window !== "undefined" ? window.innerWidth : 1280) - w - 24;
        const y = (typeof window !== "undefined" ? window.innerHeight : 720) - h - 24;
        return { x, y, w, h };
    });
    const [materialMode, setMaterialMode] = useState("beige");
    const [showGrid, setShowGrid] = useState(true);
    const [wireframe, setWireframe] = useState(false);
    // Per-mesh state: key → { visible, opacity }
    const [meshState, setMeshState] = useState({});

    // Boot the three.js scene + load every input mesh.
    useEffect(() => {
        if (items.length === 0) return;
        let disposed = false;

        (async () => {
            try {
                const THREE = await import("three");
                const { OrbitControls } = await import("three/examples/jsm/controls/OrbitControls.js");

                if (disposed) return;
                const mount = mountRef.current;
                if (!mount) return;
                const width = mount.clientWidth;
                const height = mount.clientHeight;

                // Scene + linear-tonemapped dark canvas — clinical, like 3Shape's dark mode.
                const scene = new THREE.Scene();
                scene.background = new THREE.Color(0x0f172a);
                sceneRef.current = scene;

                const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);
                camera.position.set(0, 80, 200);
                cameraRef.current = camera;

                const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
                renderer.setPixelRatio(window.devicePixelRatio);
                renderer.setSize(width, height);
                renderer.toneMapping = THREE.ACESFilmicToneMapping;
                renderer.toneMappingExposure = 1.05;
                mount.appendChild(renderer.domElement);
                rendererRef.current = renderer;

                // 3-point lighting — Exocad / 3Shape look.
                scene.add(new THREE.AmbientLight(0xffffff, 0.45));
                const key = new THREE.DirectionalLight(0xffffff, 0.9);
                key.position.set(80, 120, 100);
                scene.add(key);
                const fill = new THREE.DirectionalLight(0xb6dffa, 0.45);
                fill.position.set(-100, 30, -60);
                scene.add(fill);
                const rim = new THREE.DirectionalLight(0xfff0d8, 0.55);
                rim.position.set(0, -100, 60);
                scene.add(rim);

                // Soft grid floor (toggleable from the toolbar).
                const grid = new THREE.GridHelper(300, 30, 0x475569, 0x334155);
                grid.position.y = -50;
                grid.name = "__grid";
                scene.add(grid);

                // OrbitControls — touch-friendly.
                const controls = new OrbitControls(camera, renderer.domElement);
                controls.enableDamping = true;
                controls.dampingFactor = 0.08;
                controls.target.set(0, 0, 0);
                controls.minDistance = 5;
                controls.maxDistance = 2000;
                controlsRef.current = controls;

                // Procedural MatCap (radial light→dark sphere) so we don't ship a static image.
                const makeMatcapTexture = () => {
                    const size = 128;
                    const c = document.createElement("canvas");
                    c.width = size; c.height = size;
                    const g = c.getContext("2d");
                    const grad = g.createRadialGradient(size * 0.35, size * 0.32, size * 0.05, size * 0.5, size * 0.5, size * 0.5);
                    grad.addColorStop(0, "#ffffff");
                    grad.addColorStop(0.25, "#f4eada");
                    grad.addColorStop(0.6, "#c8b696");
                    grad.addColorStop(0.85, "#7a6648");
                    grad.addColorStop(1, "#2b2317");
                    g.fillStyle = grad;
                    g.fillRect(0, 0, size, size);
                    const tex = new THREE.CanvasTexture(c);
                    tex.needsUpdate = true;
                    return tex;
                };

                // Parent group that owns every loaded mesh — moves them together
                // when the user rotates, while keeping each mesh independently
                // styleable.
                const group = new THREE.Group();
                groupRef.current = group;
                scene.add(group);

                // Load each input mesh in parallel.
                const loaded = await Promise.all(items.map(async (item, idx) => {
                    const ext = (item.filename || "").split(".").pop().toLowerCase();
                    if (!SUPPORTED.has(ext)) {
                        throw new Error(`Unsupported format: .${ext}`);
                    }
                    let LoaderClass;
                    if (ext === "stl") {
                        ({ STLLoader: LoaderClass } = await import("three/examples/jsm/loaders/STLLoader.js"));
                    } else if (ext === "ply") {
                        ({ PLYLoader: LoaderClass } = await import("three/examples/jsm/loaders/PLYLoader.js"));
                    } else {
                        ({ OBJLoader: LoaderClass } = await import("three/examples/jsm/loaders/OBJLoader.js"));
                    }
                    const resp = await fetch(item.url);
                    if (!resp.ok) throw new Error(`HTTP ${resp.status} on ${item.filename}`);
                    const buf = ext === "obj" ? await resp.text() : await resp.arrayBuffer();
                    const loader = new LoaderClass();

                    // OBJ → THREE.Group (may contain multiple sub-meshes).
                    // STL/PLY → BufferGeometry.
                    let three;
                    let hasColors = false;
                    if (ext === "obj") {
                        three = loader.parse(buf);
                    } else {
                        const geom = loader.parse(buf);
                        if (geom.computeVertexNormals) geom.computeVertexNormals();
                        // PLY has `color` attribute if the scanner exported vertex colors.
                        hasColors = !!(geom.attributes && (geom.attributes.color || geom.attributes.uv));
                        const tmpMat = new THREE.MeshPhongMaterial({ color: 0xe6d2b5 });
                        three = new THREE.Mesh(geom, tmpMat);
                    }
                    // OBJ files: detect vertex colors on any child geometry.
                    if (ext === "obj") {
                        three.traverse((c) => {
                            if (c.isMesh && c.geometry?.attributes?.color) hasColors = true;
                        });
                    }

                    const key = `m-${idx}`;
                    return {
                        key,
                        threeMesh: three,
                        ext,
                        hasColors,
                        label: item.label || item.filename || `Mesh ${idx + 1}`,
                        filename: item.filename,
                        jaw: item.jaw || guessJaw(item.filename),
                    };
                }));

                if (disposed) return;
                loaded.forEach((m) => group.add(m.threeMesh));

                // Materials are built per-mesh once, then we just swap which one
                // is active when the user toggles "Beige / Clay / Original". Keeps
                // GC cheap and lets us preserve per-mesh opacity across mode changes.
                const matcapTex = makeMatcapTexture();
                loaded.forEach((m) => {
                    const setMaterials = (meshLike) => {
                        const beige = new THREE.MeshPhongMaterial({
                            color: 0xe6d2b5,
                            specular: 0x4a3f30,
                            shininess: 28,
                            transparent: true,
                            opacity: 1,
                            side: THREE.DoubleSide,
                        });
                        const matcap = new THREE.MeshMatcapMaterial({
                            matcap: matcapTex,
                            transparent: true,
                            opacity: 1,
                            side: THREE.DoubleSide,
                        });
                        const original = new THREE.MeshPhongMaterial({
                            color: m.hasColors ? 0xffffff : 0xcfd8dc,
                            vertexColors: m.hasColors,
                            specular: 0x333333,
                            shininess: 60,
                            transparent: true,
                            opacity: 1,
                            side: THREE.DoubleSide,
                        });
                        meshLike.userData.materials = { beige, matcap, original };
                        meshLike.material = beige;
                    };
                    if (m.threeMesh.isMesh) setMaterials(m.threeMesh);
                    else m.threeMesh.traverse((c) => { if (c.isMesh) setMaterials(c); });
                });
                meshesRef.current = loaded;

                // Centre the GROUP (not each child) so upper/lower stay aligned.
                const box = new THREE.Box3().setFromObject(group);
                const size = box.getSize(new THREE.Vector3()).length() || 100;
                const centre = box.getCenter(new THREE.Vector3());
                group.position.sub(centre);
                fitDistanceRef.current = size * 1.4;
                camera.position.set(0, size * 0.35, fitDistanceRef.current);
                camera.lookAt(0, 0, 0);
                controls.update();

                // Initial per-mesh state — everyone visible at 100% opacity.
                const initial = {};
                loaded.forEach((m) => { initial[m.key] = { visible: true, opacity: 1 }; });
                setMeshState(initial);

                const animate = () => {
                    rafRef.current = requestAnimationFrame(animate);
                    controls.update();
                    renderer.render(scene, camera);
                };
                animate();

                const onResize = () => {
                    if (!mount) return;
                    const w = mount.clientWidth;
                    const h = mount.clientHeight;
                    renderer.setSize(w, h);
                    camera.aspect = w / h;
                    camera.updateProjectionMatrix();
                };
                window.addEventListener("resize", onResize);
                renderer.__onResize = onResize;
                setLoading(false);
            } catch (e) {
                setError(e?.message || "Failed to load");
                setLoading(false);
            }
        })();

        return () => {
            disposed = true;
            cancelAnimationFrame(rafRef.current);
            const r = rendererRef.current;
            if (r) {
                if (r.__onResize) window.removeEventListener("resize", r.__onResize);
                r.dispose();
                if (r.domElement && r.domElement.parentNode) r.domElement.parentNode.removeChild(r.domElement);
            }
            controlsRef.current?.dispose?.();
            sceneRef.current?.traverse?.((o) => {
                if (o.geometry) o.geometry.dispose();
                if (o.material) {
                    if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
                    else o.material.dispose();
                }
                if (o.userData?.materials) {
                    Object.values(o.userData.materials).forEach((m) => m.dispose?.());
                }
            });
        };
    }, [items]);

    // Display-mode side effects: keep body scroll locked in fullscreen, bind
    // Escape to step back to inline, persist PiP geometry, and trigger the
    // renderer's resize handler whenever the container size changes.
    useEffect(() => {
        // Default: no scroll lock for inline + pip (pip floats, doesn't fill viewport).
        const prevOverflow = document.body.style.overflow;
        if (isFs) document.body.style.overflow = "hidden";

        const onKey = (e) => { if (e.key === "Escape" && (isFs || isPip)) setMode("inline"); };
        if (isFs || isPip) window.addEventListener("keydown", onKey);

        // Tell the saved onResize handler to recompute after the DOM repaints.
        // Two timers: one immediately for the entrance animation, one after the
        // CSS transition completes (~250ms) to catch the final layout.
        const tid1 = setTimeout(() => rendererRef.current?.__onResize?.(), 60);
        const tid2 = setTimeout(() => rendererRef.current?.__onResize?.(), 320);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKey);
            clearTimeout(tid1); clearTimeout(tid2);
            setTimeout(() => rendererRef.current?.__onResize?.(), 60);
        };
    }, [mode, isFs, isPip]);

    // Resize the WebGL canvas when the PiP box geometry changes (drag/resize).
    useEffect(() => {
        if (!isPip) return undefined;
        const tid = setTimeout(() => rendererRef.current?.__onResize?.(), 30);
        try {
            localStorage.setItem("stlviewer_pip", JSON.stringify(pipBox));
        } catch (e) { /* ignore */ }
        return () => clearTimeout(tid);
    }, [pipBox, isPip]);

    // Whenever the material mode changes, swap the active material on every loaded mesh.
    useEffect(() => {
        meshesRef.current.forEach((m) => {
            const apply = (target) => {
                const mats = target.userData?.materials;
                if (!mats) return;
                const next = mats[materialMode] || mats.beige;
                if (next) {
                    // Preserve opacity + wireframe + visibility across material swaps.
                    next.opacity = target.material?.opacity ?? 1;
                    next.transparent = next.opacity < 1;
                    next.wireframe = wireframe;
                    target.material = next;
                    target.material.needsUpdate = true;
                }
            };
            if (m.threeMesh.isMesh) apply(m.threeMesh);
            else m.threeMesh.traverse((c) => { if (c.isMesh) apply(c); });
        });
    }, [materialMode, wireframe]);

    // Per-mesh visibility + opacity.
    useEffect(() => {
        meshesRef.current.forEach((m) => {
            const s = meshState[m.key] || { visible: true, opacity: 1 };
            m.threeMesh.visible = s.visible;
            const apply = (target) => {
                if (!target.material) return;
                target.material.opacity = s.opacity;
                target.material.transparent = s.opacity < 1;
                target.material.depthWrite = s.opacity >= 0.99;
                target.material.needsUpdate = true;
            };
            if (m.threeMesh.isMesh) apply(m.threeMesh);
            else m.threeMesh.traverse((c) => { if (c.isMesh) apply(c); });
        });
    }, [meshState]);

    // Grid floor toggle.
    useEffect(() => {
        const scene = sceneRef.current;
        if (!scene) return;
        const grid = scene.getObjectByName("__grid");
        if (grid) grid.visible = showGrid;
    }, [showGrid]);

    const setMeshKey = useCallback((key, patch) => {
        setMeshState((prev) => ({ ...prev, [key]: { ...(prev[key] || { visible: true, opacity: 1 }), ...patch } }));
    }, []);

    const applyPreset = (preset) => {
        const cam = cameraRef.current;
        const c = controlsRef.current;
        if (!cam || !c) return;
        const d = fitDistanceRef.current;
        const [x, y, z] = preset.dir;
        cam.position.set(x * d, y * d * 0.85, z * d);
        cam.lookAt(0, 0, 0);
        c.target.set(0, 0, 0);
        c.update();
    };

    const resetCamera = () => {
        const cam = cameraRef.current;
        const c = controlsRef.current;
        if (!cam || !c) return;
        const d = fitDistanceRef.current;
        cam.position.set(0, d * 0.35, d);
        cam.lookAt(0, 0, 0);
        c.target.set(0, 0, 0);
        c.update();
    };

    const downloadAll = () => {
        // Single mesh: triggers a normal download.
        // Multi-mesh bite view: fires one download per file (browsers stagger them).
        if (items.length === 0) return;
        items.forEach((m, idx) => {
            setTimeout(() => {
                const a = document.createElement("a");
                a.href = m.url;
                a.download = m.filename || `model-${idx + 1}`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }, idx * 250);
        });
    };

    // PiP drag handler — clamps to viewport bounds so the user can't lose the
    // window off-screen. Uses pointer events for unified mouse + touch.
    const startDrag = (e) => {
        e.preventDefault();
        const startX = e.clientX, startY = e.clientY;
        const orig = pipBox;
        const onMove = (ev) => {
            const nx = Math.max(0, Math.min(window.innerWidth - orig.w, orig.x + (ev.clientX - startX)));
            const ny = Math.max(0, Math.min(window.innerHeight - 40, orig.y + (ev.clientY - startY)));
            setPipBox({ ...orig, x: nx, y: ny });
        };
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    };
    const startResize = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX, startY = e.clientY;
        const orig = pipBox;
        const onMove = (ev) => {
            const nw = Math.max(320, Math.min(window.innerWidth - orig.x - 8, orig.w + (ev.clientX - startX)));
            const nh = Math.max(240, Math.min(window.innerHeight - orig.y - 8, orig.h + (ev.clientY - startY)));
            setPipBox({ ...orig, w: nw, h: nh });
        };
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    };

    const anyHasColors = meshesRef.current.some((m) => m.hasColors);
    const isBite = meshesRef.current.length > 1;

    // Choose the wrapping container's classes + inline style per mode.
    // - inline: in-flow, fixed 520px tall.
    // - fullscreen: covers the viewport, max z-index.
    // - pip: free-floating draggable+resizable window via portal-to-body so it
    //   survives layout changes in the page underneath.
    const rootClass = isFs
        ? "fixed inset-0 z-[100] rounded-none border-0 shadow-2xl bg-slate-900 overflow-hidden"
        : isPip
            ? "fixed z-[110] rounded-2xl border border-slate-700 shadow-2xl bg-slate-900 overflow-hidden ring-1 ring-cyan-500/30"
            : "relative rounded-2xl border border-slate-200 h-[520px] bg-slate-900 overflow-hidden";
    const rootStyle = isPip ? { left: pipBox.x, top: pipBox.y, width: pipBox.w, height: pipBox.h } : undefined;

    const inner = (
        <div data-testid="stl-viewer" data-mode={mode} className={rootClass} style={rootStyle}>
            {isPip && (
                // Drag handle strip at the very top (above the toolbar) so dragging
                // doesn't accidentally hit any toolbar button. 8px tall.
                <div
                    onPointerDown={startDrag}
                    data-testid="viewer-pip-drag-handle"
                    title="Drag to move"
                    className="absolute top-0 left-0 right-0 h-2 z-30 bg-cyan-500/40 hover:bg-cyan-500/60 cursor-grab active:cursor-grabbing"
                />
            )}
            {/* TOP TOOLBAR */}
            <div className="absolute top-0 left-0 right-0 z-20 bg-slate-900/90 backdrop-blur px-4 py-2.5 border-b border-slate-700 flex items-center gap-3 flex-wrap">
                <div className="text-[12.5px] font-mono-tr text-slate-100 truncate min-w-0 flex-1">
                    {isBite ? `Bite · ${meshesRef.current.length} meshes` : (items[0]?.filename || "Mesh")}
                </div>

                {/* Material mode toggle */}
                <div className="inline-flex rounded-lg border border-slate-700 overflow-hidden" data-testid="viewer-material-toggle">
                    {MATERIAL_MODES.map((m) => {
                        const Icon = m.icon;
                        const disabled = m.id === "original" && !anyHasColors;
                        return (
                            <button
                                key={m.id}
                                onClick={() => !disabled && setMaterialMode(m.id)}
                                disabled={disabled}
                                title={disabled ? "Original colors require PLY/OBJ with vertex colors" : `Switch to ${m.label} material`}
                                data-testid={`viewer-mode-${m.id}`}
                                className={`px-2.5 py-1.5 inline-flex items-center gap-1 text-[11.5px] font-medium border-r border-slate-700 last:border-r-0 transition ${
                                    materialMode === m.id ? "bg-cyan-500 text-[#022c33]" : "text-slate-300 hover:bg-slate-800"
                                } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                            >
                                <Icon size={12} /> {m.label}
                            </button>
                        );
                    })}
                </div>

                <button
                    onClick={() => setWireframe((v) => !v)}
                    data-testid="viewer-wireframe"
                    title="Toggle wireframe"
                    className={`p-1.5 rounded-md ${wireframe ? "bg-cyan-500 text-[#022c33]" : "text-slate-300 hover:bg-slate-800"}`}
                >
                    <BoxIcon size={15} />
                </button>
                <button
                    onClick={() => setShowGrid((v) => !v)}
                    data-testid="viewer-grid"
                    title="Toggle grid floor"
                    className={`p-1.5 rounded-md ${showGrid ? "bg-slate-700 text-slate-100" : "text-slate-300 hover:bg-slate-800"}`}
                >
                    <Grid3X3 size={15} />
                </button>
                <button onClick={resetCamera} data-testid="viewer-reset" title="Reset view" className="p-1.5 rounded-md text-slate-300 hover:bg-slate-800"><RotateCcw size={15} /></button>
                <button onClick={downloadAll} data-testid="viewer-download" title={items.length > 1 ? `Download all ${items.length} meshes` : "Download"} className="p-1.5 rounded-md text-slate-300 hover:bg-slate-800"><Download size={15} /></button>
                <button
                    onClick={() => setMode(isPip ? "inline" : "pip")}
                    data-testid="viewer-pip"
                    title={isPip ? "Dock back inline (Esc)" : "Pop out (Picture-in-picture)"}
                    className={`p-1.5 rounded-md transition ${isPip ? "bg-cyan-500 text-[#022c33]" : "text-slate-300 hover:bg-slate-800"}`}
                >
                    <PictureInPicture2 size={15} />
                </button>
                <button
                    onClick={() => setMode(isFs ? "inline" : "fullscreen")}
                    data-testid="viewer-fullscreen"
                    title={isFs ? "Exit fullscreen (Esc)" : "Enter fullscreen"}
                    className={`p-1.5 rounded-md transition ${isFs ? "bg-cyan-500 text-[#022c33]" : "text-slate-300 hover:bg-slate-800"}`}
                >
                    {isFs ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                </button>
                {onClose && (
                    <button onClick={onClose} data-testid="viewer-close" title="Close" className="p-1.5 rounded-md text-slate-300 hover:bg-slate-800"><X size={15} /></button>
                )}
            </div>

            {/* LEFT SIDE-PANEL — per-mesh controls (only when ≥1 mesh loaded) */}
            {meshesRef.current.length > 0 && !loading && (
                <div className="absolute top-[60px] left-3 z-20 w-64 bg-slate-900/90 backdrop-blur border border-slate-700 rounded-xl p-3 space-y-2.5" data-testid="viewer-mesh-panel">
                    <div className="font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-400 mb-1">· Meshes ({meshesRef.current.length})</div>
                    {meshesRef.current.map((m) => {
                        const s = meshState[m.key] || { visible: true, opacity: 1 };
                        return (
                            <div key={m.key} className="rounded-lg border border-slate-700 bg-slate-800/40 p-2" data-testid={`mesh-row-${m.key}`}>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setMeshKey(m.key, { visible: !s.visible })}
                                        title={s.visible ? "Hide" : "Show"}
                                        data-testid={`mesh-toggle-${m.key}`}
                                        className={`p-1 rounded ${s.visible ? "text-cyan-300" : "text-slate-500"}`}
                                    >
                                        {s.visible ? <Eye size={13} /> : <EyeOff size={13} />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[12px] text-slate-100 truncate font-medium">{m.jaw ? (m.jaw === "upper" ? "Upper jaw" : "Lower jaw") : m.label}</div>
                                        <div className="text-[10px] text-slate-500 font-mono-tr truncate">{m.filename} · .{m.ext}{m.hasColors ? " · color" : ""}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 mt-1.5">
                                    <span className="text-[10px] text-slate-500 font-mono-tr w-12">Opacity</span>
                                    <input
                                        type="range"
                                        min="10"
                                        max="100"
                                        value={Math.round(s.opacity * 100)}
                                        onChange={(e) => setMeshKey(m.key, { opacity: Number(e.target.value) / 100 })}
                                        className="flex-1 accent-cyan-500"
                                        disabled={!s.visible}
                                        data-testid={`mesh-opacity-${m.key}`}
                                    />
                                    <span className="text-[10px] font-mono-tr text-slate-400 w-9 text-right">{Math.round(s.opacity * 100)}%</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* RIGHT SIDE-PANEL — view presets */}
            {!loading && (
                <div className="absolute top-[60px] right-3 z-20 bg-slate-900/90 backdrop-blur border border-slate-700 rounded-xl p-2 grid grid-cols-2 gap-1.5" data-testid="viewer-view-presets">
                    {VIEW_PRESETS.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => applyPreset(p)}
                            data-testid={`view-preset-${p.id}`}
                            className="px-3 py-1.5 rounded-md text-[11.5px] font-medium text-slate-300 hover:bg-slate-800 hover:text-cyan-300"
                            title={`${p.label} view`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            )}

            {/* BOTTOM HINT */}
            <div className="absolute bottom-0 left-0 right-0 z-10 bg-slate-900/90 backdrop-blur px-4 py-2 border-t border-slate-700 text-[11px] text-slate-400 font-mono-tr tracking-wide flex items-center justify-between flex-wrap gap-2">
                <span>· Drag to rotate · Scroll to zoom · Right-drag to pan</span>
                <span className="inline-flex items-center gap-1"><ZoomIn size={11} /> 3D viewer · WebGL · {materialMode}</span>
            </div>

            {loading && (
                <div className="absolute inset-0 flex items-center justify-center z-30 bg-slate-900/70">
                    <div className="flex items-center gap-2 text-cyan-200 text-[13px]"><Loader2 className="animate-spin" size={16} /> Loading model{items.length > 1 ? "s" : ""}…</div>
                </div>
            )}
            {error && (
                <div className="absolute inset-0 flex items-center justify-center z-30 bg-slate-900/80 text-rose-300 text-[13.5px]">
                    Could not render preview · {error}
                </div>
            )}

            <div ref={mountRef} className="absolute inset-0" />

            {isPip && (
                // Bottom-right resize grip — 16px square, pure CSS triangle.
                <div
                    onPointerDown={startResize}
                    data-testid="viewer-pip-resize-handle"
                    title="Drag to resize"
                    className="absolute bottom-0 right-0 w-4 h-4 z-30 cursor-nwse-resize bg-cyan-500/60 hover:bg-cyan-400"
                    style={{ clipPath: "polygon(100% 0, 100% 100%, 0 100%)" }}
                />
            )}
        </div>
    );

    // PiP renders into a portal at document.body so it floats above page-level
    // layout (modals, banners, sticky headers). Fullscreen + inline render in-place.
    return isPip && typeof document !== "undefined" ? createPortal(inner, document.body) : inner;
}
